import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'
import { useSentry } from '@common/error-monitoring-layer/composables/useSentry'
import { watch } from 'vue'
import { getActivePinia, type Pinia } from 'pinia'
import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useLogger } from '@common/core-layer/composables/useLogger'
import { clearAuthCookie, clearAuthCookieForEvent, getAuthCookie, setAuthCookie } from '@common/core-layer/utils/cookie.utils'
import {
  asSsrAuthEvent,
  type ServerRequestEvent,
  type SsrAuthEvent,
  type SsrAuthEventOptions,
} from '@common/core-layer/utils/auth-cookie.server.utils'
import { clearAdminPreviewAgencyToken, clearAdminV2PreviewEnvelope } from '@common/core-layer/utils/admin-preview-token.utils'
import { clearAdminPreviewMarkerCookie } from '@common/core-layer/utils/admin-preview-cookie.utils'
import { tryUseNuxtApp, useRoute } from 'nuxt/app'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'
import { trackRefreshFailureAutoLogout } from '@common/analytics-layer/utils/track-auto-logout.utils'
import {
  fingerprintRefreshToken,
  resolveAutoLogoutDevice,
  resolveAutoLogoutFlavour,
} from '@common/analytics-layer/tracking/auto-logout-envelope'
import type { AuthCookieData } from '@common/core-layer/utils/auth-cookie.utils'
import { AuthStatus, useAuthStore } from '../store/auth.store'

import { AuthService } from '../services/auth.service'
import { OTPService } from '../services/otp.service'
import { getDeviceInfo } from '../../core-layer/utils'
import { useLocation } from '../../location-layer/composables/useLocation'
import { useLocationStore } from '../../location-layer/store/location.store'
import { MAGIC_LINK_REQUEST_SOURCE_CLIENTPORTAL_PREVIEW_V2, MigrationStatus, isMagicLinkOtpHandoff } from '../types'
import { MigrationUtility } from '../utils/migration'
import { resolveSessionIdFromAuthResponse } from '../utils/session.utils'
import { getJwtExpiryMs } from '../utils/ssr-token-refresh.utils'
import type { QueryClient } from '@tanstack/vue-query'
import type {
  AuthResponse,
  AuthResult,
  CheckPasswordSetResponse,
  RefreshTokenOptions,
  RefreshTokenResponse,
  RefreshValidationResult,
  SaveAuthAfterLoginOptions,
  TokenRefreshResult,
  ValidatedRefreshAuthCookie,
  UseAuthSendOtpPayload,
  UseAuthSetNewPasswordPayload,
  UseAuthSetPasswordPayload,
  UseAuthSignUpPayload,
} from '../types'

/** Shared across `useAuth()` calls so concurrent proactive refreshes collapse into one (client-only). */
let inFlightProactiveRefresh: Promise<TokenRefreshResult> | null = null

const readRouteQueryString = (value: unknown): string | undefined => {
  if (Array.isArray(value)) return typeof value[0] === 'string' && value[0].trim() ? value[0] : undefined
  if (typeof value === 'string' && value.trim()) return value
  return undefined
}

const isSafeInternalRedirectPath = (url: string): boolean =>
  url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\') && !url.includes('://')

/** Resolve the H3 SSR event from an explicit caller or ambient Nuxt SSR context. */
function resolveSsrAuthEvent(options?: SsrAuthEventOptions): SsrAuthEvent | undefined {
  let event = options?.ssrEvent
  if (!event && process.server) {
    // tryUseNuxtApp avoids NUXT_E1001/nostics noise when ambient context is missing
    const nuxtApp = tryUseNuxtApp()
    event = asSsrAuthEvent(nuxtApp?.ssrContext?.event)
  }
  return event
}

/** Clear persisted auth on the client cookie or SSR response + event context. */
function clearAuthCookieForSession(event?: SsrAuthEvent): void {
  if (process.server && event?.node?.res) {
    clearAuthCookieForEvent(event)
    if (event.context?.auth) {
      delete event.context.auth
    }
    return
  }

  clearAuthCookie()
}

/**
 * Only `redirect` (and other non-credential query params) should be forwarded.
 */
const AUTH_CREDENTIAL_QUERY_KEYS = new Set(['token', 'loginCode', 'sessionKey', 'email'])

function stripAuthCredentialsFromQuery(query: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {}
  for (const [key, value] of Object.entries(query)) {
    if (AUTH_CREDENTIAL_QUERY_KEYS.has(key)) continue
    if (value == null) continue
    if (Array.isArray(value)) {
      const first = value.find(v => typeof v === 'string') as string | undefined
      if (first) cleaned[key] = first
      continue
    }
    if (typeof value === 'string') cleaned[key] = value
  }
  return cleaned
}

export function useAuth(passedPinia?: Pinia) {
  const authStore = useAuthStore(passedPinia)

  /**
   * Get the current location ID based on platform mode
   * - GoKollab: Uses default GoKollab location data
   * - WhiteLabel: Uses location from location store
   */
  const getDefaultLocationId = (): string => {
    const { isGokollab } = useGlobal()

    if (isGokollab) {
      const { getDefaultGokollabLocationData } = useLocation()
      return getDefaultGokollabLocationData().locationId as string
    }

    const { locationId } = useLocation()
    return locationId.value as string
  }

  // Initialize services with current location
  const authService = new AuthService(getDefaultLocationId())
  const otpService = new OTPService()

  function registerAuth(token: string, userId: string, contactId: string, sessionUniqueKey?: string): { success: boolean } {
    authStore.setAuth(token, userId, contactId, sessionUniqueKey)

    // Set Sentry user context
    try {
      const sentryHelpers = useSentry()
      sentryHelpers.setUser(userId, contactId)
    } catch {
      // Sentry not available - continue without error
    }

    return { success: true }
  }

  /**
   * Finalize a deferred-auth login after `setPassword` succeeds. Prefers fresh
   * credentials from the set-password response; falls back to the original
   * deferred login data stashed in `authStore.pendingAuthData` when the
   * set-password response does not carry a token.
   *
   * Returns the `AuthResult` from `saveAuthAfterLogin` (so callers can honour
   * `redirected`), or null if there is no usable auth data (caller should
   * surface an error in that case).
   */
  async function finalizeDeferredAuthAfterPasswordSet(setPasswordResponse?: AuthResponse | null): Promise<AuthResult | null> {
    const fresh = setPasswordResponse?.token ? setPasswordResponse : null
    const data = fresh ?? authStore.pendingAuthData
    if (!data?.token) {
      authStore.setPendingAuthData(null)
      return null
    }
    authStore.setPendingAuthData(null)
    return await saveAuthAfterLogin(data)
  }

  /**
   * Clear authentication from store and Sentry
   */
  function deregisterAuth(): void {
    authStore.clearAuth()

    // Clear Sentry user context
    try {
      const sentryHelpers = useSentry()
      sentryHelpers.clearUser()
    } catch {
      // Sentry not available - continue without error
    }
  }

  async function saveAuthAfterLogin(data: AuthResponse, options?: SaveAuthAfterLoginOptions): Promise<AuthResult> {
    if (!data?.token) {
      throw new Error('Login failed - no token received')
    }

    // Use locationId from response, or fall back to current location
    const locationId = data.locationId || getDefaultLocationId()

    const sessionUniqueKey = resolveSessionIdFromAuthResponse(data)

    // Set auth cookie
    await setAuthCookie({
      token: data.token,
      refreshToken: data.refreshToken,
      locationId: locationId,
      userId: data.userId,
      contactId: data.contactId,
      externalUserId: data.externalUserId,
      sessionUniqueKey,
    })

    // Update auth store
    registerAuth(data.token, data.userId, data.contactId, sessionUniqueKey)

    // Mutual exclusion: a real learner session was just established, so wipe lingering courses
    // admin-preview artifacts (`acatv2`, `acatv2-agency`, `capm`) — the single funnel for every
    // login method. Client-only, no-op on SSR (unaffected `performMagicLinkLoginServer` path).
    // Pinia store intentionally not reset here (common must not depend on courses layer) —
    // clearing these signals is enough; the `00.` middleware re-hydrates from them and finds nothing.
    clearAdminV2PreviewEnvelope()
    clearAdminPreviewAgencyToken()
    clearAdminPreviewMarkerCookie()

    // Load logged-in user profile
    try {
      const { ensureLoaded } = useCurrentUser()
      if (ensureLoaded) {
        await ensureLoaded()
      }
    } catch (e) {
      console.error('Failed to load logged-in user profile:', e)
    }

    if (process.client && (await handleMigrationGate(data))) {
      return { success: true, data, redirected: true }
    }

    if (options?.skipPostLoginRedirect) {
      return { success: true, data, redirected: false }
    }

    const didRedirect = await handleRedirectAfterLogin()

    return { success: true, data, redirected: didRedirect }
  }

  /**
   * Client-only: if the login response carries a pending migration status, navigate
   * to the matching gate page and return true. Otherwise return false so the caller
   * continues with the normal redirect flow.
   *
   * Migration state is persisted via `MigrationUtility` (localStorage) instead of
   * `history.state` so a reload, deep-link, or partial completion still keeps the
   * user on the gate. The redirect-guard middleware reads the same store.
   * Only `isGokollab` is honoured per spec — GoKollab apps do not run this flow.
   */
  async function handleMigrationGate(data: AuthResponse): Promise<boolean> {
    const { isGokollab } = useGlobal()

    if (isGokollab) {
      // Gate disabled for this app — clear any stale state from a prior session
      // and let the caller continue with the normal redirect.
      MigrationUtility.clear()
      return false
    }

    const route = useRoute()
    const { CPNavigateTo } = useCPNavigation()
    // Strip one-time auth credentials (`token` / `loginCode` / `sessionKey` / `email`)
    // before forwarding to the gate page. The auth cookie is already set at this
    // point, so leaving these in the URL would leak them into the address bar and
    // browser history of `/expired-password` and `/profile-confirmation`. Only
    // `redirect` (and any other non-credential params) should survive.
    const sanitizedQuery = stripAuthCredentialsFromQuery(route.query as Record<string, unknown>)
    const gateQuery = Object.keys(sanitizedQuery).length > 0 ? { query: sanitizedQuery } : {}

    if (data.migrationStatus === MigrationStatus.CREATED) {
      MigrationUtility.save({
        migrationStatus: MigrationStatus.CREATED,
        migrationUserId: data.userId,
        email: data.email,
        contactId: data.contactId,
        passwordResetToken: data.passwordResetToken,
      })
      await CPNavigateTo({
        name: 'auth-expired-password',
        ...gateQuery,
        options: { replace: true },
      })
      return true
    }
    if (data.migrationStatus === MigrationStatus.PASSWORD_CONFIRMED) {
      MigrationUtility.save({
        migrationStatus: MigrationStatus.PASSWORD_CONFIRMED,
        migrationUserId: data.userId,
        email: data.email,
      })
      await CPNavigateTo({
        name: 'auth-profile-confirmation',
        ...gateQuery,
        options: { replace: true },
      })
      return true
    }

    MigrationUtility.clear()
    return false
  }

  async function migrateEmail(email: string): Promise<void> {
    if (!email) return
    await MigrationUtility.migrate(email, getDefaultLocationId())
  }

  /**
   * Login with email and password.
   */
  async function loginWithEmailPassword(email: string, password: string, captchaToken?: string): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const locationId = getDefaultLocationId()

      // Perform login
      const { data } = await authService.loginWithEmailPassword({
        email,
        password,
        locationId,
        captchaToken,
      })

      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      console.error('Login failed:', errorMessage)
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Login with Google OAuth
   */
  async function loginWithGoogle(googleToken: string): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await authService.loginWithSocialID({
        authProvider: 'google',
        idToken: googleToken,
        locationId: getDefaultLocationId(),
      })

      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  // Legacy login function for backward compatibility
  const login = loginWithEmailPassword

  async function generateOTPForLogin(
    email: string,
    action: string,
    captchaToken?: string
  ): Promise<{ data: { otpToken: string; isNewUser?: boolean } }> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await otpService.generateOTPForLogin({
        email,
        action,
        captchaToken,
        locationId: getDefaultLocationId(),
      })

      return { data }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'OTP generation failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Verify OTP for login
   */
  async function verifyOTPForLogin(
    email: string,
    action: string,
    otp: number,
    token: string,
    options?: AuthDeferInPlacePasswordOptions & { captchaToken?: string }
  ): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await authService.verifyOTPForLogin({
        email,
        action,
        otp,
        token,
        locationId: getDefaultLocationId(),
        captchaToken: options?.captchaToken,
      })

      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'OTP verification failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Check a signup OTP without deleting it. Signup is the only consume.
   */
  async function validateOtp(email: string, otp: number, token: string): Promise<{ data: { valid: boolean } }> {
    const { data } = await otpService.validateOtp({
      email,
      otp,
      token,
      locationId: getDefaultLocationId(),
    })

    return { data }
  }

  /**
   * Send OTP for signup or resend
   */
  async function sendOtp(payload: UseAuthSendOtpPayload): Promise<{ data: { otpToken: string; isNewUser?: boolean } }> {
    const { data } = await authService.sendOtp({
      email: payload.email,
      locationId: getDefaultLocationId(),
      action: payload.action,
      captchaToken: payload.captchaToken,
      type: payload.type,
      fullName: payload.fullName,
    })

    return { data }
  }

  async function signUp(payload: UseAuthSignUpPayload): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await authService.signUp({
        email: payload.email,
        password: payload.password,
        username: payload.username,
        locationId: getDefaultLocationId(),
        otp: payload.otp,
        otpToken: payload.otpToken,
        redirectUrl: payload.redirectUrl,
        captchaToken: payload.captchaToken,
        referredBy: payload.referredBy,
        signupPageUrl: payload.signupPageUrl,
      })

      const result = await saveAuthAfterLogin(data)
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Signup failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  async function setPassword(payload: UseAuthSetPasswordPayload): Promise<{ data: AuthResponse }> {
    // For deferred-auth flows the user is NOT yet `authStore.isAuthenticated`,
    // so `BaseService` won't attach an Authorization header. Pass the pending
    // login token explicitly here — and ONLY here — so this single endpoint
    // sees it without granting full session access elsewhere in the app.
    const pendingToken = !authStore.isAuthenticated && authStore.pendingAuthData?.token ? authStore.pendingAuthData.token : undefined

    const { data } = await authService.setPassword(
      {
        passwordResetToken: payload.passwordResetToken,
        contactId: payload.contactId,
        newPassword: payload.newPassword,
        confirmPassword: payload.confirmPassword,
        locationId: getDefaultLocationId(),
        captchaToken: payload.captchaToken,
      },
      pendingToken ? { authToken: pendingToken } : undefined
    )

    return { data }
  }

  async function setNewPassword(payload: UseAuthSetNewPasswordPayload): Promise<{ data: AuthResponse }> {
    const { data } = await authService.setNewPassword({
      locationId: getDefaultLocationId(),
      emailId: payload.emailId,
      newPassword: payload.newPassword,
      confirmPassword: payload.confirmPassword,
      loginCode: payload.loginCode,
      token: payload.token,
    })

    return { data }
  }

  async function preflightCheckPassword(params: {
    email?: string
    userId?: string
    loginCode?: string
    token?: string
  }): Promise<CheckPasswordSetResponse | null> {
    try {
      const { data } = await authService.checkIfPasswordSet({
        locationId: getDefaultLocationId(),
        email: params.email,
        userId: params.userId,
        loginCode: params.loginCode,
        token: params.token,
      })
      return data
    } catch (error: unknown) {
      console.warn('[useAuth] checkIfPasswordSet failed; proceeding to login anyway:', error)
      return null
    }
  }

  async function loginWithLoginCode(
    email: string | undefined,
    loginCode: string,
    redirectUrl?: string | null,
    externalUserId?: string
  ): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)
      const locationId = getDefaultLocationId()
      const deviceInfo = getDeviceInfo()

      let resolvedEmail = email
      // The /auth forwarding flow allows emailless ?loginCode= URLs (the
      // backend resolves the user from the code). Preflight resolves the email;
      // the login API still exchanges the code for a session.
      if (email || externalUserId || loginCode) {
        const check = await preflightCheckPassword({ email, userId: externalUserId, loginCode })
        if (check?.email) resolvedEmail = check.email
      }

      const { data } = await authService.loginWithLoginCode({
        email: resolvedEmail,
        loginCode,
        locationId,
        domainName: deviceInfo.domainName,
        redirectUrl: redirectUrl ?? null,
      })
      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Login with magic link token.
   *
   * Pre-flight: when a `token` (or external `userId`) is present we call
   * `checkIfPasswordSet` to resolve the canonical email server-side.
   * Expired tokens are not fail-fast: the login API initiates OTP and returns
   * `requiresOtp` so the host can open VERIFY_SECURE_CODE. Expired or consumed
   * Event notification tokens take the same OTP handoff path.
   */
  async function loginWithMagicLink(
    email: string | undefined,
    token: string,
    redirectUrl?: string | null,
    externalUserId?: string
  ): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)
      const locationId = getDefaultLocationId()
      const deviceInfo = getDeviceInfo()
      const { isClientportal } = useGlobal()

      let resolvedEmail = email
      let eventNotificationRedirect: string | null = null
      if (token || externalUserId) {
        const check = await preflightCheckPassword({ token, userId: externalUserId })
        if (check?.email) resolvedEmail = check.email
        if (check?.tokenType === 'event_notification') {
          // The query redirect is client input: only an internal path may override the server-resolved destination.
          const queryRedirect = redirectUrl && isSafeInternalRedirectPath(redirectUrl) ? redirectUrl : null
          eventNotificationRedirect = queryRedirect || check.eventNotificationRedirect || null
          if (check.userId && authStore.isAuthenticated && authStore.userId === check.userId) {
            const redirected = eventNotificationRedirect ? await navigateToUrl(eventNotificationRedirect) : false
            return { success: true, redirected }
          }
        }
      }

      const { data } = await authService.loginWithMagicLink({
        email: resolvedEmail,
        token,
        locationId,
        domainName: deviceInfo.domainName,
        redirectUrl: eventNotificationRedirect ?? redirectUrl ?? null,
        ...(isClientportal ? { otpHandoffOnExpire: true, eventNotificationOtpRecovery: true } : {}),
      })

      if (isMagicLinkOtpHandoff(data)) {
        authStore.setStatus(AuthStatus.UNAUTHENTICATED)
        return {
          success: true,
          requiresOtp: true,
          otpHandoff: {
            email: data.email,
            otpToken: data.otpToken,
          },
          redirected: false,
        }
      }

      const result = await saveAuthAfterLogin(data, {
        skipPostLoginRedirect: Boolean(eventNotificationRedirect),
      })
      // Event notification logins prefer an internal query redirect, falling back to the server-resolved destination.
      if (eventNotificationRedirect && !result.redirected) {
        return { ...result, redirected: await navigateToUrl(eventNotificationRedirect) }
      }
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Login with session key (URL-based SSO).
   */
  async function loginWithSessionKey(email: string | undefined, sessionKey: string, redirectUrl?: string | null): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)
      const locationId = getDefaultLocationId()
      const deviceInfo = getDeviceInfo()
      const { data } = await authService.loginWithSessionKey({
        email,
        sessionKey,
        locationId,
        domainName: deviceInfo.domainName,
        redirectUrl: redirectUrl ?? null,
      })
      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  async function performMagicLinkLoginServer(params: {
    email?: string
    loginCode?: string
    token?: string
    sessionKey?: string
    locationId: string
    domainName: string
    event: ServerRequestEvent
  }): Promise<AuthResponse> {
    const { email, loginCode, token, sessionKey, locationId, domainName, event } = params
    const hasSessionKey = sessionKey !== undefined && sessionKey !== ''
    const hasLoginCode = loginCode !== undefined && loginCode !== ''
    let data: AuthResponse
    if (hasSessionKey) {
      ;({ data } = await authService.loginWithSessionKey({ email, sessionKey: sessionKey!, locationId, domainName, redirectUrl: null }))
    } else if (hasLoginCode) {
      ;({ data } = await authService.loginWithLoginCode({ email, loginCode: loginCode!, locationId, domainName, redirectUrl: null }))
    } else {
      ;({ data } = await authService.loginWithMagicLink({ email, token: token!, locationId, domainName, redirectUrl: null }))
    }

    if (!data?.token || !data?.contactId) {
      throw new Error('Login failed - no token received')
    }

    const sessionUniqueKey = resolveSessionIdFromAuthResponse(data)

    await setAuthCookie(
      {
        token: data.token,
        refreshToken: data.refreshToken,
        locationId: data.locationId,
        userId: data.userId,
        contactId: data.contactId,
        externalUserId: data.externalUserId,
        sessionUniqueKey,
      },
      {},
      event
    )

    return data
  }

  /**
   * Send forgot password email
   */
  async function forgotPassword(email: string, captchaToken?: string): Promise<{ data: { success: boolean } }> {
    const { data } = await authService.forgotPassword({
      email,
      locationId: getDefaultLocationId(),
      captchaToken,
    })

    return { data }
  }

  async function requestMagicLink(token: string): Promise<{ data: { success: boolean } }> {
    return authService.requestMagicLink({
      token,
      locationId: getDefaultLocationId(),
      source: MAGIC_LINK_REQUEST_SOURCE_CLIENTPORTAL_PREVIEW_V2,
    })
  }

  /**
   * Validate reset password link (optional, for pre-submit validation)
   */
  async function validateResetLink(userId: string, token: string): Promise<{ data: { success: boolean } }> {
    const { data } = await authService.validateResetLink({
      userId,
      token,
      locationId: getDefaultLocationId(),
    })
    return { data }
  }

  /**
   * Reset password with userId and token from reset link
   */
  async function resetPassword(
    userId: string,
    token: string,
    password: string,
    revokeAllSessions?: boolean
  ): Promise<{ data: { success: boolean } }> {
    const { data } = await authService.resetPassword({
      userId,
      token,
      password,
      locationId: getDefaultLocationId(),
      revokeAllSessions,
    })
    return { data }
  }

  async function cleanupClientStateAfterLogout(): Promise<void> {
    if (!process.client) return

    const queryClient = tryUseNuxtApp()?.$queryClient as QueryClient | undefined
    try {
      // Reset active queries so mounted UI gets a fresh logged-out fetch cycle.
      await queryClient?.resetQueries({ type: 'active' })
    } catch (error) {
      console.warn('Failed to reset active queries during logout:', error)
    }

    const pinia = getActivePinia()
    if (pinia && '_s' in pinia) {
      const storeMap = (
        pinia as Pinia & {
          _s: Map<
            string,
            {
              $id?: string
              $reset?: () => void
              reset?: () => void
              resetState?: () => void
              clear?: () => void
              clearState?: () => void
            }
          >
        }
      )._s
      storeMap.forEach((store, storeId) => {
        // Preserve location context so domain/location config survives logout.
        if (storeId === 'location' || store.$id === 'location') {
          return
        }
        try {
          // Prefer built-in Pinia reset for options stores.
          if (typeof store.$reset === 'function') {
            store.$reset()
            return
          }

          // Fallback for setup-style stores that expose custom reset/clear actions.
          if (typeof store.reset === 'function') {
            store.reset()
            return
          }
          if (typeof store.resetState === 'function') {
            store.resetState()
            return
          }
          if (typeof store.clearState === 'function') {
            store.clearState()
            return
          }
          if (typeof store.clear === 'function') {
            store.clear()
          }
        } catch {
          // Ignore store-specific reset failures and continue with remaining stores.
        }
      })
    }

    try {
      // Remove inactive queries to drop stale cache entries no longer observed.
      queryClient?.removeQueries({ type: 'inactive' })
    } catch (error) {
      console.warn('Failed to remove inactive queries during logout:', error)
    }
  }

  async function logout(options?: SsrAuthEventOptions): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      const event = resolveSsrAuthEvent(options)

      if (process.client) {
        const cookieData = getAuthCookie()
        if (cookieData?.locationId && cookieData?.sessionUniqueKey) {
          const locationStore = useLocationStore()
          const { isGokollab } = useGlobal()
          try {
            await authService.updateSession({
              locationId: cookieData.locationId,
              sessionUniqueKey: cookieData.sessionUniqueKey,
              isV2: isGokollab || locationStore.isAuthV2Enabled,
            })
          } catch (error) {
            console.error('Failed to update session during logout:', error)
          }
        }
      }

      clearAuthCookieForSession(event)

      // Clear auth store
      deregisterAuth()

      // Drop any persisted migration gate state so the next user / session starts
      // clean and the redirect guard doesn't bounce them onto a stale gate.
      MigrationUtility.clear()

      await cleanupClientStateAfterLogout()

      return { success: true }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed'
      authStore.setError(errorMessage)
      console.error('Logout failed:', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Check if user is authenticated (from cookie)
   */
  async function checkAuth(): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)

      // Only read cookies on client side
      if (process.server) {
        return { success: false }
      }

      const authData = getAuthCookie()

      // Token and contactId are required
      if (!authData?.token || !authData?.contactId) {
        deregisterAuth()
        return { success: false }
      }

      // Use userId if available, otherwise fallback to contactId
      const userId = authData.userId || authData.contactId
      const sessionUniqueKey = resolveSessionIdFromAuthResponse(authData)
      registerAuth(authData.token, userId, authData.contactId, sessionUniqueKey)

      return { success: true }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication check failed'
      console.error('[checkAuth] Authentication check failed:', errorMessage)
      deregisterAuth()
      authStore.setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Refresh authentication token.
   *
   * SSR fallback: callers invoked from nitro handlers outside composable setup
   * (where `useNuxtApp()` throws) may pass `options.ssrEvent` — the H3 request
   * event — directly. It is used preferentially over the ambient nuxtApp for
   * reading `event.context.auth` (populated by the server auth middleware) and
   * writing the refreshed `Set-Cookie` header via `setAuthCookie`.
   *
   * Failure policy: SSR failures never clear the cookie (client recovery decides);
   * client failures adopt a concurrently rotated cookie when present, and any
   * other failure logs out — duplicate refreshes are gone, so a failed refresh
   * means the session is genuinely unusable.
   */
  function readRefreshAuthPayload(event: SsrAuthEvent | undefined): AuthCookieData | null {
    if (process.server && event?.context?.auth) {
      return event.context.auth
    }
    return getAuthCookie()
  }

  function validateRefreshAuthPayload(payload: AuthCookieData | null): RefreshValidationResult {
    if (!payload?.token || (!payload?.contactId && !payload?.userId)) {
      console.warn('[refreshToken] No auth data found')
      return { ok: false, error: 'No auth data found' }
    }
    if (!payload.refreshToken) {
      console.warn('[refreshToken] Missing refreshToken')
      return { ok: false, error: 'No refresh token found' }
    }
    return {
      ok: true,
      payload: {
        token: payload.token,
        refreshToken: payload.refreshToken,
        // ClientPortal contact sessions store no top-level userId in the
        // cookie — fall back to contactId, mirroring checkAuth. The refresh
        // endpoint itself only needs refreshToken + locationId; userId is
        // only used to re-register the store afterwards.
        userId: payload.userId || payload.contactId,
        contactId: payload.contactId,
        locationId: payload.locationId,
        sessionUniqueKey: payload.sessionUniqueKey,
      },
    }
  }

  async function applySuccessfulTokenRefresh(
    data: RefreshTokenResponse,
    catPayload: ValidatedRefreshAuthCookie,
    locationId: string,
    event: SsrAuthEvent | undefined
  ): Promise<string> {
    const refreshedSessionKey = resolveSessionIdFromAuthResponse(data)
    const updatedPayload: AuthCookieData = {
      ...catPayload,
      token: data.token,
      refreshToken: data.refreshToken || catPayload.refreshToken,
      locationId,
      ...(refreshedSessionKey ? { sessionUniqueKey: refreshedSessionKey } : {}),
    }

    await setAuthCookie(updatedPayload, {}, event)

    if (process.server && event?.context) {
      event.context.auth = updatedPayload
    }

    registerAuth(data.token, catPayload.userId, catPayload.contactId, refreshedSessionKey ?? resolveSessionIdFromAuthResponse(catPayload))

    return data.token
  }

  /** App type for the auto-logout envelope. Returns undefined when no Pinia context is active. */
  function readAppTypeForAutoLogout(): unknown {
    try {
      return useGlobal().appType
    } catch {
      return undefined
    }
  }

  /**
   * Emits the auto-logout diagnostic for a client-side refresh failure.
   *
   * Called immediately BEFORE `logout()` at every client branch that ends the session, because
   * `logout()` clears the cookie and store these identity fields are read from. Both branches go
   * through this one helper so their envelopes cannot diverge.
   *
   * Self-guarded on top of the emitter's own guard: analytics must never be able to skip logout.
   */
  function emitRefreshFailureAutoLogout(params: {
    payload?: ValidatedRefreshAuthCookie
    locationId?: string
    error: string
    status?: number
    previousReq?: RefreshTokenOptions['previousReq']
  }): void {
    try {
      trackRefreshFailureAutoLogout({
        locationId: params.locationId || params.payload?.locationId,
        contactId: params.payload?.contactId,
        // Flavour and device are decoration on this event, and both read ambient context that can
        // legitimately be unavailable (`useGlobal` needs an active Pinia; `navigator` is absent on
        // the server). Resolve each behind its own guard so a context miss degrades the field
        // instead of costing us the whole diagnostic.
        flavour: resolveAutoLogoutFlavour(readAppTypeForAutoLogout()),
        device: resolveAutoLogoutDevice(typeof navigator === 'undefined' ? undefined : navigator.userAgent),
        ...(params.previousReq ? { previousReq: params.previousReq } : {}),
        refresh: {
          ...(params.status === undefined ? {} : { statusCode: params.status }),
          error: params.error,
          fingerprint: fingerprintRefreshToken(params.payload?.refreshToken),
        },
      })
    } catch (emitError: unknown) {
      const { logger } = useLogger()
      logger.warn('[refreshToken] Auto-logout analytics emit failed; continuing with logout', {
        labels: { context: 'useAuth.emitRefreshFailureAutoLogout' },
        error: emitError instanceof Error ? emitError : new Error(String(emitError)),
      })
    }
  }

  async function refreshToken(options?: RefreshTokenOptions): Promise<TokenRefreshResult> {
    const { logger } = useLogger()
    const event = resolveSsrAuthEvent(options)

    const validation = validateRefreshAuthPayload(readRefreshAuthPayload(event))
    if (!validation.ok) {
      logger.warn('[refreshToken] Refresh skipped — no usable auth payload', {
        labels: { context: 'useAuth.refreshToken', reason: validation.error, side: process.server ? 'server' : 'client' },
      })
      return { success: false, error: validation.error }
    }

    // Server callers (nitro stacks where useLocation is empty) fall back to the
    // locationId already present in the validated cookie payload.
    const locationId = process.server ? validation.payload.locationId || getDefaultLocationId() : getDefaultLocationId()
    if (!locationId) {
      logger.warn('[refreshToken] No locationId resolved; client session will be cleared', {
        labels: { context: 'useAuth.refreshToken', side: process.server ? 'server' : 'client' },
      })
      console.warn('[refreshToken] No locationId from useLocation')
      if (process.client) {
        // No HTTP call was made, so no refresh status exists to report.
        emitRefreshFailureAutoLogout({
          payload: validation.payload,
          error: 'No location id',
          previousReq: options?.previousReq,
        })
        await logout()
      }
      return { success: false, error: 'No location id' }
    }

    try {
      const { data } = await authService.refreshToken({
        refreshToken: validation.payload.refreshToken,
        locationId,
      })

      if (!data?.token) {
        throw new Error('Token refresh failed')
      }

      const token = await applySuccessfulTokenRefresh(data, validation.payload, locationId, event)
      logger.info('[refreshToken] Token refresh succeeded', {
        labels: { context: 'useAuth.refreshToken', side: process.server ? 'server' : 'client' },
      })
      return { success: true, token }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed'
      const status =
        (error as { status?: number } | undefined)?.status ?? (error as { response?: { status?: number } } | undefined)?.response?.status
      console.error('Token refresh failed:', errorMessage)
      logger.warn('[refreshToken] Token refresh failed', {
        labels: {
          context: 'useAuth.refreshToken',
          side: process.server ? 'server' : 'client',
          http_status: status === undefined ? 'none' : String(status),
        },
        error: error instanceof Error ? error : new Error(errorMessage),
      })

      // SSR: never destroy the session from a refresh. Cookie writes on a possibly
      // streamed response are unreliable, and the browser still holds a cookie that
      // client-side recovery can use — clearing here was the logout-loop trigger.
      if (process.server) {
        logger.warn('[refreshToken] Server-side refresh failed; session left intact for client recovery', {
          labels: { context: 'useAuth.refreshToken', http_status: status === undefined ? 'none' : String(status) },
        })
        return { success: false, error: errorMessage }
      }

      // Any remaining client-side failure ends the session. Duplicate refreshes
      // no longer occur (middleware owns SSR refresh, single-flight owns client),
      // and rotation races are absorbed by backend leeway — a failed refresh
      // means the token is genuinely unusable.
      logger.warn('[refreshToken] Refresh rejected; logging out', {
        labels: { context: 'useAuth.refreshToken', http_status: status === undefined ? 'none' : String(status), outcome: 'logout' },
      })
      emitRefreshFailureAutoLogout({
        payload: validation.payload,
        locationId,
        error: errorMessage,
        status,
        previousReq: options?.previousReq,
      })
      await logout()
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Adopt the auth cookie's token when it outlives the store's.
   *
   * The store is hydrated from the cookie once at boot, but refreshes can land in
   * the cookie only — another tab, the client portal, or the cookie-only boot
   * refresh (`auth-refresh.client`). Without this, an open tab keeps sending its
   * boot-time token until it expires even though a valid one sits in the cookie.
   *
   * A cookie for a different contact means another login in a sibling tab; we
   * never switch identities under a live session. Returns true when adopted.
   */
  function syncTokenFromCookie(): boolean {
    if (!process.client) return false
    const cookie = getAuthCookie()
    const storeToken = authStore.token
    if (!cookie?.token || cookie.token === storeToken) return false
    if (authStore.contactId && cookie.contactId !== authStore.contactId) return false

    const cookieExpiry = getJwtExpiryMs(cookie.token)
    const storeExpiry = storeToken ? getJwtExpiryMs(storeToken) : null
    if (cookieExpiry === null || (storeExpiry !== null && cookieExpiry <= storeExpiry)) return false

    registerAuth(cookie.token, cookie.userId || cookie.contactId, cookie.contactId, resolveSessionIdFromAuthResponse(cookie))
    return true
  }

  /**
   * Best-effort proactive refresh for the Meet SPA.
   *
   * The meeting-client SDK holds its own axios instance with no 401->refresh
   * interceptor, so Meet refreshes ahead of expiry instead: when the access token
   * is within `windowMs` of expiry, refresh it (after first adopting a fresher
   * cookie token, which makes the refresh unnecessary when another tab did it).
   *
   * Single-flight on the client: concurrent callers (e.g. parallel SDK requests
   * via `resolveMeetAuthToken`) share one refresh — and so one refresh-token
   * rotation.
   *
   * Unlike `refreshToken`, this NEVER logs out: a failed proactive refresh must
   * leave the session untouched and let the reactive paths handle genuine expiry.
   *
   * Client-only, and requires the location store to be hydrated so
   * `authService.refreshToken` resolves the correct Auth V1/V2 path — callers must
   * run it after `init-auth`/`init-location` (SDK requests only start after mount).
   */
  function refreshTokenIfExpiringSoon(windowMs: number): Promise<TokenRefreshResult> {
    if (!process.client) {
      return Promise.resolve({ success: false, error: 'client-only' })
    }
    syncTokenFromCookie()
    // Module-level state is client-only (guarded above) — never shared across SSR requests.
    inFlightProactiveRefresh ??= (async () => {
      try {
        return await runProactiveRefresh(windowMs)
      } finally {
        inFlightProactiveRefresh = null
      }
    })()
    return inFlightProactiveRefresh
  }

  async function runProactiveRefresh(windowMs: number): Promise<TokenRefreshResult> {
    const { logger } = useLogger()

    const currentToken = authStore.token
    if (!authStore.isLoggedIn || !currentToken) {
      return { success: false, error: 'Not logged in' }
    }

    const expiryMs = getJwtExpiryMs(currentToken)
    if (expiryMs === null) {
      return { success: false, error: 'Token expiry undecodable' }
    }
    // Still comfortably fresh — nothing to do.
    if (expiryMs - Date.now() > windowMs) {
      return { success: false, error: 'Token not near expiry' }
    }

    const validation = validateRefreshAuthPayload(readRefreshAuthPayload(undefined))
    if (!validation.ok) {
      return { success: false, error: validation.error }
    }

    try {
      // Prefer the cookie's locationId (always present), mirroring the server
      // branch of `refreshToken`, so we do not depend on the location store for
      // the id itself. Resolved inside the try so a throwing getDefaultLocationId
      // fallback degrades to a best-effort {success:false}, never a rejection.
      const locationId = validation.payload.locationId || getDefaultLocationId()
      if (!locationId) {
        return { success: false, error: 'No location id' }
      }

      const { data } = await authService.refreshToken({
        refreshToken: validation.payload.refreshToken,
        locationId,
      })
      if (!data?.token) {
        throw new Error('Token refresh failed')
      }

      if (getAuthCookie()?.refreshToken !== validation.payload.refreshToken) {
        return { success: false, error: 'Session changed during refresh' }
      }
      const token = await applySuccessfulTokenRefresh(data, validation.payload, locationId, undefined)
      logger.info('[refreshTokenIfExpiringSoon] Proactive token refresh succeeded', {
        labels: { context: 'useAuth.refreshTokenIfExpiringSoon' },
      })
      return { success: true, token }
    } catch (error: unknown) {
      // Best-effort: the current token is still valid, so keep the session intact
      // and let the reactive paths recover on genuine expiry. Never logout here.
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed'
      const status =
        (error as { status?: number } | undefined)?.status ?? (error as { response?: { status?: number } } | undefined)?.response?.status
      logger.warn('[refreshTokenIfExpiringSoon] Proactive refresh failed; keeping current token', {
        labels: {
          context: 'useAuth.refreshTokenIfExpiringSoon',
          http_status: status === undefined ? 'none' : String(status),
        },
        error: error instanceof Error ? error : new Error(errorMessage),
      })
      return { success: false, error: errorMessage }
    }
  }

  function setupTokenRefresh(): (() => void) | undefined {
    // Client-only: setInterval and watch require browser
    if (!process.client) return

    const REFRESH_INTERVAL_MS = 23 * 60 * 60 * 1000 // 23 hours

    const interval = setInterval(async () => {
      if (authStore.isLoggedIn && authStore.token) {
        try {
          await refreshToken()
        } catch {
          const { logger } = useLogger()
          logger.warn('[setupTokenRefresh] Scheduled refresh threw; logging out', {
            labels: { context: 'useAuth.setupTokenRefresh', outcome: 'logout' },
          })
          await logout()
        }
      }
    }, REFRESH_INTERVAL_MS)

    // Clear interval when user logs out
    watch(
      () => authStore.isLoggedIn,
      (authenticated: boolean) => {
        if (!authenticated) {
          clearInterval(interval)
        }
      }
    )

    return () => clearInterval(interval)
  }

  function clearError(): void {
    authStore.setError(null)
  }

  /**
   * Guard helper for actions that require an authenticated user.
   * Opens login modal and returns false when user is not authenticated.
   */
  function ensureAuthenticated(page: string = 'LOGIN'): boolean {
    if (authStore.isLoggedIn) {
      return true
    }

    authStore.openLoginModal(page)
    return false
  }

  /**
   * Navigate to a URL. Handles external https, internal paths (/), or no-op when url is absent.
   */
  async function navigateToUrl(url: string): Promise<boolean> {
    try {
      if (isExternalHttpsRedirect(url)) {
        // window exists only in the browser; avoid ReferenceError during SSR
        if (process.client) {
          window.location.replace(url)
          return true
        }
        return false
      }

      if (url.startsWith('/')) {
        await navigateTo(url, { replace: true })
        return true
      }
      return false
    } catch (error) {
      console.error('Error navigating to URL:', error)
      throw error
    }
  }

  /**
   * Redirect after login using query.redirect or HMI-style query.redirectUrl when present.
   * Returns true when a navigation was issued so callers can avoid a follow-up
   * fallback redirect that would otherwise clobber the intended destination.
   *
   * `redirect` keeps the existing magic-link behaviour (including trusted https).
   * `redirectUrl` is HMI leftover and is restricted to same-origin internal paths.
   */
  async function handleRedirectAfterLogin(): Promise<boolean> {
    const route = useRoute()
    const redirect = readRouteQueryString(route.query.redirect)
    if (redirect) {
      return await navigateToUrl(redirect)
    }

    const redirectUrl = readRouteQueryString(route.query.redirectUrl)
    if (!redirectUrl || !isSafeInternalRedirectPath(redirectUrl)) {
      return false
    }
    return await navigateToUrl(redirectUrl)
  }

  async function generateIAMTokenFromFirebaseToken(
    firebaseAuthToken: string,
    cookieData?: { locationId: string; userId: string; contactId: string; externalUserId: string; sessionUniqueKey: string }
  ): Promise<{ token: string; refreshToken: string }> {
    const data = await authService.generateIAMTokenFromFirebaseToken(firebaseAuthToken)

    if (data.token && cookieData) {
      await saveAuthAfterLogin({
        token: data.token,
        refreshToken: data.refreshToken,
        locationId: cookieData.locationId,
        userId: cookieData.userId,
        contactId: cookieData.contactId,
        externalUserId: cookieData.externalUserId,
        sessionUniqueKey: cookieData.sessionUniqueKey,
      })
    }

    return data
  }

  return {
    // Store
    store: authStore,

    // Auth state management
    registerAuth,

    // Login methods
    login,
    loginWithEmailPassword,
    loginWithGoogle,
    loginWithLoginCode,
    loginWithMagicLink,
    loginWithSessionKey,
    performMagicLinkLoginServer,

    // OTP methods
    generateOTPForLogin,
    verifyOTPForLogin,
    validateOtp,
    sendOtp,

    // Signup
    signUp,

    // Password methods
    setPassword,
    setNewPassword,
    forgotPassword,
    requestMagicLink,
    validateResetLink,
    resetPassword,
    finalizeDeferredAuthAfterPasswordSet,

    // Session management
    logout,
    checkAuth,
    refreshToken,
    refreshTokenIfExpiringSoon,
    syncTokenFromCookie,
    setupTokenRefresh,
    generateIAMTokenFromFirebaseToken,

    // Utilities
    clearError,
    ensureAuthenticated,
    navigateToUrl,
    handleRedirectAfterLogin,
    /**
     * Normal (post-login) flows do NOT need to call this — it's
     * already invoked inside the login methods above.
     */
    saveAuthAfterLogin,

    migrateEmail,
  }
}
